<template>
    <div class="fixed md:static" :class="{ invisible: isCitationModalOpen }">
        <transition name="transition-fade">
            <button
                v-if="onlyMobileVisible && isOpen && !isCitationModalOpen"
                :style="topPositionStyle"
                :aria-label="localizations[lang].tooltips.close"
                class="fixed left-0 -z-10 h-full w-full bg-black/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-theme-accent"
                @click="closeEmit"
            />
        </transition>
        <transition :name="animationName">
            <div
                v-if="isOpen"
                ref="focusTrapContainer"
                dusk="AdaptiveListDropdown"
                role="menu"
                :style="topPositionOnDesktop"
                :class="[
                    'text-theme-text',
                    'border-theme-border',
                    'rounded-lg',
                    'border',
                    'fixed',
                    'md:absolute',
                    'w-full',
                    'md:w-auto',
                    'left-0',
                    'md:left-auto',
                    'md:right-0',
                    'bottom-0',
                    'md:bottom-auto',
                    'md:top-10',
                    'z-[60]',
                    'shadow',
                    'md:mr-4',
                    'bg-theme-bg',
                ]"
            >
                <div v-if="onlyMobileVisible" class="h-4 w-full py-2" @mouseup="closeEmit">
                    <div class="mx-auto h-1 w-14 rounded-lg bg-theme-toolbar-icon" />
                </div>
                <div class="px-4 py-8 md:px-1 md:py-1">
                    <slot />
                </div>
            </div>
        </transition>
    </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { throttle } from 'lodash';
import Hammer from 'hammerjs';
import isTouchDevice from '../../../js/utils/isTouchDevice';
import focusTrapMixin from '../../../js/mixins/focusTrapMixin';

export default {
    mixins: [focusTrapMixin],
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close'],
    data() {
        return {
            hammerInstance: null,
            translate: 0,
            // Opened by mouse, so don't move keyboard focus into the menu when it opens.
            // (focusTrapMixin still handles ESC-to-close and restoring focus to the trigger on close.)
            focusTrapAutoFocus: false,
        };
    },
    computed: {
        ...mapState(['ui', 'localizations', 'lang']),
        ...mapGetters(['onlyMobileVisible']),
        /**
         * Drives focusTrapMixin: activates the trap (and Escape-to-close) while the dropdown is open.
         * @returns {boolean}
         */
        focusTrapEnabled() {
            return this.isOpen;
        },
        isCitationModalOpen() {
            return this.ui.citationModal?.opened ?? false;
        },
        animationName() {
            return this.onlyMobileVisible ? 'slide-bottom-transition' : 'fade-transition';
        },
        topPositionStyle() {
            return `top: ${this.ui.headerbar.offsetHeight}px`;
        },
        topPositionOnDesktop() {
            if (this.onlyMobileVisible) {
                return '';
            }

            return this.topPositionStyle;
        },
    },
    watch: {
        isOpen(value) {
            value ? this.$nextTick(() => this.closeDragging()) : this.hammerInstance?.destroy();
        },
    },
    beforeDestroy() {
        // Clean up Hammer instance if component is destroyed while open
        if (this.hammerInstance) {
            this.hammerInstance.destroy();
            this.hammerInstance = null;
        }
    },
    methods: {
        closeEmit() {
            this.$emit('close');
        },
        /**
         * focusTrapMixin hook: close the dropdown when Escape is pressed.
         * @returns {void}
         */
        onFocusTrapEscape() {
            this.closeEmit();
        },
        closeDragging() {
            if (!this.onlyMobileVisible) return;

            const element = this.$refs.focusTrapContainer;
            if (!element || this.hammerInstance) return; // Add defensive check and prevent duplicate

            this.hammerInstance = new Hammer.Manager(
                element,
                // Added to fix Hammer swipe event on Android
                { inputClass: isTouchDevice ? Hammer.TouchInput : Hammer.PointerEventInput }
            );

            const elementHeight = element.offsetHeight;
            this.hammerInstance.add(new Hammer.Pan({ direction: Hammer.DIRECTION_VERTICAL }));
            this.hammerInstance.on(
                'pandown panup panend',
                throttle(
                    e => {
                        const deltaY = e.deltaY > 0 ? e.deltaY : this.translate + e.deltaY;
                        this.translate = Math.max(0, deltaY);

                        element.style.transform = `translateY(${this.translate}px)`;
                        if (e.type !== 'panend') return;

                        if (this.translate > elementHeight / 2) {
                            this.closeEmit();
                            element.style.transform = '';
                        } else {
                            element.style.transform = 'translateY(0)';
                        }
                    },
                    // Throttle set to 34 ms (~2 frames at 60 FPS) to balance smooth UI updates
                    // and reduce performance overhead by limiting event handling frequency.
                    34,
                    {
                        leading: true,
                        trailing: true,
                    }
                )
            );
        },
    },
};
</script>
